import React from 'react';
import {Image, ImageAspectRatioPresets, TextButton, TextButtonPriority} from '@wix/wix-ui-tpa/cssVars';
import {useLocaleKeys} from '../../../locale-keys/LocaleKeys';
import {classes} from './StoreNameAndLogo.st.css';
import classNames from 'clsx';
import {CheckoutHeaderModel} from '../../../domain/models/common/CheckoutHeader.model';
import {useNavigationLinkProps} from '../../../components/Checkout/Hooks/useNavigationLinkProps';
import {NavigationTarget} from '../../../domain/preload/messages';

export interface StoreNameAndLogoProps {
  continueShoppingUrl?: string;
  clickOnContinueShopping?: () => void;
  logoUrl?: string;
  storeName?: string;
  checkoutHeader?: CheckoutHeaderModel;
  isOneColumnView?: boolean;
  titleSuffixOverride?: string;
}

export enum StoreNameAndLogoDataHooks {
  root = 'StoreNameAndLogoDataHooks.root',
  logo = 'StoreNameAndLogoDataHooks.logo',
  storeName = 'StoreNameAndLogoDataHooks.storeName',
  title = 'StoreNameAndLogoDataHooks.title',
}

export enum Alignment {
  SIDE = 'SIDE',
  CENTER = 'CENTER',
}

export enum LogoSize {
  SMALL = 'SMALL',
  MEDIUM = 'MEDIUM',
  LARGE = 'LARGE',
}

export const StoreNameAndLogo = ({
  continueShoppingUrl,
  clickOnContinueShopping,
  logoUrl,
  storeName,
  checkoutHeader,
  isOneColumnView,
  titleSuffixOverride,
}: // eslint-disable-next-line sonarjs/cognitive-complexity
StoreNameAndLogoProps) => {
  const localKeys = useLocaleKeys();
  const navigationLinkProps = useNavigationLinkProps({
    target: NavigationTarget.Home,
    url: continueShoppingUrl,
    onClick: () => clickOnContinueShopping?.(),
  });
  const hasCheckoutHeader = !!checkoutHeader;
  const logoImage = checkoutHeader?.logo?.image;
  const titleLogo = logoImage?.filename ? logoImage.filename : logoUrl;
  const text = checkoutHeader?.text;
  const titleText = text || storeName;

  const defaultTitle = titleSuffixOverride ?? localKeys.checkout.header_default_title();
  const titleSuffix = titleSuffixOverride ?? localKeys.checkout.header.store_title_suffix();

  const storeNameAndLogoTitle = titleText || titleLogo ? titleSuffix : defaultTitle;
  const logoSize = checkoutHeader?.logo?.size;
  const alignment = checkoutHeader?.alignment;

  const shouldShowLogo = hasCheckoutHeader ? !!checkoutHeader?.logo : !!titleLogo;
  const shouldShowStoreName = !shouldShowLogo;
  const shouldDisplayCheckoutLabel = hasCheckoutHeader ? checkoutHeader?.displayCheckoutLabel : true;

  const logoHeight = () => {
    /* istanbul ignore next */
    if (logoSize === LogoSize.MEDIUM) {
      return isOneColumnView ? 56 : 80;
    }
    /* istanbul ignore next */
    if (logoSize === LogoSize.LARGE) {
      return isOneColumnView ? 80 : 148;
    }
    /* istanbul ignore next */
    return isOneColumnView ? 26 : 56;
  };

  const checkoutTitle = () => {
    if ((!titleText && shouldShowStoreName) || (!titleLogo && shouldShowLogo)) {
      return defaultTitle;
    }
    if (shouldShowLogo && !shouldDisplayCheckoutLabel) {
      return titleSuffixOverride ? titleSuffix : undefined;
    }
    return titleSuffix;
  };

  return (
    <div
      data-hook={StoreNameAndLogoDataHooks.root}
      className={classNames(
        alignment === Alignment.CENTER
          ? /* istanbul ignore next */ classes.headerTitleAndLogoCenter
          : classes.headerTitleAndLogoSide,
        classes.headerTitleAndLogoContent,
        !hasCheckoutHeader && classes.headerTitleAndLogo
      )}>
      {shouldShowLogo && titleLogo && (
        <a
          {...navigationLinkProps}
          data-hook={StoreNameAndLogoDataHooks.logo}
          className={!hasCheckoutHeader ? classes.logo : undefined}>
          {hasCheckoutHeader ? (
            <Image
              src={titleLogo}
              width={logoHeight()}
              height={logoHeight()}
              alt={localKeys.checkout.header.businessLogo.altText({storeName})}
            />
          ) : (
            <Image
              src={titleLogo}
              aspectRatio={ImageAspectRatioPresets.square}
              fluid={true}
              alt={localKeys.checkout.header.businessLogo.altText({storeName})}
            />
          )}
        </a>
      )}
      {shouldShowStoreName && (hasCheckoutHeader || titleText) && (
        <TextButton
          as={'a'}
          priority={TextButtonPriority.primary}
          {...navigationLinkProps}
          data-hook={StoreNameAndLogoDataHooks.storeName}
          className={classes.headerStoreName}>
          {titleText}
        </TextButton>
      )}
      <h1
        className={classes.headerTitle}
        data-hook={StoreNameAndLogoDataHooks.title}
        aria-label={checkoutTitle() || storeNameAndLogoTitle}>
        {checkoutTitle()}
      </h1>
    </div>
  );
};
